import { ApiError } from './api.js';

/**
 * The mock's simulated order lifecycle, behind the same interface as orders.js, so every screen
 * of the handoff is clickable end to end for review. Installed only with ?stubHost=1 (see app.js),
 * so it can never reach a deployed iframe.
 *
 * The lifecycle is the mock's, to the millisecond: create resolves after 780 ms with a
 * PBG-CP-YYYY-NNNNNN reference; status is processing at step 0, then 1 at 1.4 s, 2 at 2.8 s, and
 * completed at 4.2 s. With forceFail the FIRST lifecycle fails at 4.2 s -- after processing,
 * never before (cert 1.22) -- and the retry succeeds, as the mock's does.
 */
export const STUB_TIMING = Object.freeze({ createMs: 780, step1Ms: 1400, step2Ms: 2800, doneMs: 4200, actionMs: 500 });

export function createStubOrders({ forceFail = false, now = () => Date.now(), timing = STUB_TIMING, seed = [] } = {}) {
  const orders = new Map();
  let failNext = forceFail;

  // Orders that already exist when the iframe opens (the stub origin's existing order).
  for (const s of seed) {
    orders.set(s.transactionId, {
      transactionId: s.transactionId, referenceNumber: s.referenceNumber, correlationId: corrId(),
      order: s.order, startedAt: s.startedAt ?? now(), willFail: false, cancelled: false,
      pendingChangeUtc: s.pendingChangeUtc ?? null,
    });
  }

  const find = (transactionId) => {
    const order = orders.get(transactionId);
    if (!order) throw new ApiError({ status: 404, errorCode: 'NOT_FOUND', correlationId: corrId() });
    return order;
  };

  return {
    stub: true,
    canCreate: true,
    canRedraw: true,
    canCancel: true,
    pollIntervalMs: 300,

    async createOrder(_deps, { order }) {
      await delay(timing.createMs);
      const correlationId = corrId();
      const transactionId = `txn_${correlationId}`;
      const referenceNumber = `PBG-CP-${new Date(now()).getFullYear()}-${String(100000 + Math.floor(Math.random() * 899999))}`;
      orders.set(transactionId, {
        transactionId, referenceNumber, correlationId, order,
        startedAt: now(), willFail: failNext, cancelled: false,
      });
      failNext = false;
      return { transactionId, referenceNumber, correlationId };
    },

    async getOrderStatus(_deps, { transactionId }) {
      const o = find(transactionId);
      const base = { transactionId, referenceNumber: o.referenceNumber, correlationId: o.correlationId };
      if (o.cancelled) return { ...base, status: 'cancelled', updatedUtc: iso(now()) };
      const elapsed = now() - o.startedAt;
      if (elapsed >= timing.doneMs) {
        if (o.willFail) return { ...base, status: 'failed', updatedUtc: iso(o.startedAt + timing.doneMs) };
        return {
          ...base,
          status: 'completed',
          updatedUtc: iso(o.startedAt + timing.doneMs),
          documents: (o.order?.documents ?? []).map((d) => ({
            id: d.id, name: d.name, target: d.type ?? 'Closing Docs', deliveredUtc: iso(o.startedAt + timing.doneMs),
          })),
        };
      }
      const step = elapsed >= timing.step2Ms ? 2 : elapsed >= timing.step1Ms ? 1 : 0;
      const status = { ...base, status: 'processing', step, updatedUtc: iso(now()) };
      if (o.pendingChangeUtc) {
        // One of three copies of this sentence (app.js's test fixture is another). Source of
        // truth is PendingChangeView.NotAppliedMessage in
        // src/MLIntegration.Channels.Encompass.Epc/Ui/OrderStatusView.cs -- a copy change there
        // needs updating here too.
        status.pendingChange = {
          receivedUtc: o.pendingChangeUtc,
          message: 'We received your change. It has not been applied to this order yet — contact PBG if you need it reflected before your documents are prepared.',
        };
      }
      return status;
    },

    async redraw(_deps, { transactionId }) {
      await delay(timing.actionMs);
      const o = find(transactionId);
      o.startedAt = now();
      o.willFail = false;
      o.cancelled = false;
      return { transactionId, referenceNumber: o.referenceNumber, correlationId: o.correlationId };
    },

    async cancelOrder(_deps, { transactionId }) {
      await delay(timing.actionMs);
      find(transactionId).cancelled = true;
    },
  };
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));
const iso = (ms) => new Date(ms).toISOString();
const corrId = () => `epc-${Math.random().toString(36).slice(2, 8)}-${Math.random().toString(36).slice(2, 6)}`;
